<template>
  <NuxtLink
    v-if="to"
    :to="to"
    :class="wrapperClass"
    @click="handleClick"
  >
    <!-- Dark mode variant: shows different images for dark/light mode -->
    <template v-if="darkModeVariant">
      <img
        class="dark:hidden w-auto"
        :class="[heightClass, imgClass]"
        :src="darkSrc"
        :alt="alt"
        loading="lazy"
      />

      <img
        class="not-dark:hidden w-auto"
        :class="[heightClass, imgClass]"
        :src="lightSrc"
        :alt="alt"
        loading="lazy"
      />
    </template>

    <!-- Single image variant: shows one image for all modes -->
    <img
      v-else
      class="w-auto"
      :class="[heightClass, imgClass]"
      :src="src"
      :alt="alt"
      loading="lazy"
    />
  </NuxtLink>

  <div
    v-else
    :class="wrapperClass"
    @click="handleClick"
  >
    <!-- Dark mode variant: shows different images for dark/light mode -->
    <template v-if="darkModeVariant">
      <img
        class="dark:hidden w-auto"
        :class="[heightClass, imgClass]"
        :src="darkSrc"
        :alt="alt"
        loading="lazy"
      />

      <img
        class="not-dark:hidden w-auto"
        :class="[heightClass, imgClass]"
        :src="lightSrc"
        :alt="alt"
        loading="lazy"
      />
    </template>

    <!-- Single image variant: shows one image for all modes -->
    <img
      v-else
      class="w-auto"
      :class="[heightClass, imgClass]"
      :src="src"
      :alt="alt"
      loading="lazy"
    />
  </div>
</template>

<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { useStaticAssetUrl } from '~/composables/useStaticAssetUrl'

// withDefaults()'s default-value object is evaluated at module load time by
// Vue's compiler (hoisted out of setup()), before any Nuxt app context
// exists -- a composable that needs useRuntimeConfig() (as useStaticAssetUrl
// does) cannot be called there (NUXT_E1001, composable called outside Nuxt
// context). darkSrc/lightSrc/src default to the plain relative path (no
// composable call); useStaticAssetUrl() resolves each to its final origin
// via the computed()s below instead, which run inside setup()'s real
// execution context. No current caller overrides these three props.
const props = withDefaults(
  defineProps<{
    /** Native click listener forwarded to the active wrapper. */
    onClick?: HTMLAttributes['onClick']
    /** Accessible alternative text for the logo. */
    alt?: string
    /** Whether to select a dark-mode logo variant. */
    darkModeVariant?: boolean
    /** Dark-mode image source (a relative path, resolved via useStaticAssetUrl). */
    darkSrc?: string
    /** CSS class controlling image height. */
    heightClass?: string
    /** CSS classes applied to the image. */
    imgClass?: string
    /** Light-mode image source (a relative path, resolved via useStaticAssetUrl). */
    lightSrc?: string
    /** Default image source (a relative path, resolved via useStaticAssetUrl). */
    src?: string
    /** Optional link destination. */
    to?: string
    /** CSS classes applied to the wrapper. */
    wrapperClass?: string
  }>(),
  {
    // Link destination (if provided, wraps logo in NuxtLink)
    alt: 'Mealz',

    // Enable dark mode variant (shows different images for dark/light mode)
    /** Dark-mode logo variant selector. */
    darkModeVariant: false,

    /** Logo source used in dark mode. */
    darkSrc: '/public/images/mealz_logo_web.svg',

    // Height class for the logo (e.g., 'h-8', 'h-10', 'h-12')
    /** Classes controlling logo height. */
    heightClass: 'h-8',

    // Additional classes for the image element
    /** Classes applied to the logo image. */
    imgClass: '',

    /** Logo source used in light mode. */
    lightSrc: '/public/images/mealz_logo_web.svg',
    onClick: undefined,

    /** Default logo source. */
    src: '/public/images/mealz_logo_web.svg',

    /** Link destination for the logo wrapper. */
    to: '',

    // Additional classes for the wrapper element
    /** Classes applied to the logo wrapper. */
    wrapperClass: ''
  }
)

const handleClick: NonNullable<HTMLAttributes['onClick']> = (event) => props.onClick?.(event)

const darkSrc = computed(() => useStaticAssetUrl(props.darkSrc))
const lightSrc = computed(() => useStaticAssetUrl(props.lightSrc))
const src = computed(() => useStaticAssetUrl(props.src))

defineOptions({ name: 'AppLogo' })
</script>
